import { createReducer, on } from "@ngrx/store";
import { C3HardwareItem, C3Order, C3OrderedProduct, C3Product, C3ProductBillOfMaterialsLineItem, C3ProductVersion, C3VendorHardwareData, EditC3ProductState, InventoryTransaction, MergeArrayValues, Resource, SaveStatus, ViewC3ProductState } from "@prism/prism-shared";
import { C3Actions } from ".";

export interface C3State {
    hardwareItems: C3HardwareItem[];
    vendorHardwareData: C3VendorHardwareData[];

    products: C3Product[];
    productVersions: C3ProductVersion[];

    orders: C3Order[];
    orderedProducts: C3OrderedProduct[];

    inventoryTransactions: InventoryTransaction[];

    billOfMaterialsLineItems: C3ProductBillOfMaterialsLineItem[];

    viewProductState: ViewC3ProductState;
    editProductState: EditC3ProductState;

    loadingState: number
    error: any,
}

export const initialState: C3State = {
    hardwareItems: [],
    vendorHardwareData: [],

    products: [],
    productVersions: [],

    orders: [],
    orderedProducts: [],

    inventoryTransactions: [],

    billOfMaterialsLineItems: [],

    viewProductState: {
        selectedProduct: null as unknown as C3Product,
        selectedProductVersions: [],
        billOfMaterialLineItems: [],
        selectedDrawingResource: null,
        productVersionsReferencingSelectedProduct: [],
        selectedProductVersionId: '',
    },

    editProductState: {
        product: null as unknown as C3Product,
        productVersion: null as unknown as C3ProductVersion,
        billOfMaterialLineItems: [],
        billOfMaterialLineItemIdsToDelete: [],
        saveStatus: SaveStatus.Saved
    },

    loadingState: 0,
    error: null
}

function applyQuantityChanges<T extends { id: string; qtyOnHand: number }>(
    items: T[],
    quantityChangeDict: Record<string, number>,
): T[] {
    if (!Object.keys(quantityChangeDict).length) {
        return items;
    }

    return items.map((item) => {
        const change = quantityChangeDict[item.id] ?? 0;

        if (!change) {
            return item;
        }

        return {
            ...item,
            qtyOnHand: (item.qtyOnHand ?? 0) + change,
        };
    });
}

function applyQuantityChange<T extends { id: string; qtyOnHand: number }>(
    item: T,
    quantityChangeDict: Record<string, number>,
): T {
    if (!item) {
        return item;
    }

    const change = quantityChangeDict[item.id] ?? 0;

    if (!change) {
        return item;
    }

    return {
        ...item,
        qtyOnHand: (item.qtyOnHand ?? 0) + change,
    };
}

export const c3Reducer = createReducer(
    initialState,
    // =========================================================
    on(C3Actions.SetHardwareItem.initial, (state): C3State => ({
        ...state,
        loadingState: state.loadingState + 1,
        error: null,
    })),
    on(C3Actions.SetHardwareItem.success, (state, { hardwareItem }): C3State => ({
        ...state,
        loadingState: state.loadingState - 1,
        hardwareItems: MergeArrayValues(state.hardwareItems, hardwareItem),
        error: null,
    })),
    on(C3Actions.SetHardwareItem.failure, (state, { error }): C3State => ({
        ...state,
        loadingState: state.loadingState - 1,
        error,
    })),
    // =========================================================
    on(C3Actions.LoadHardwareItems.initial, (state): C3State => ({
        ...state,
        loadingState: state.loadingState + 1,
        error: null,
    })),
    on(C3Actions.LoadHardwareItems.success, (state, { hardwareItems }): C3State => ({
        ...state,
        loadingState: state.loadingState - 1,
        hardwareItems,
        error: null
    })),
    on(C3Actions.LoadHardwareItems.failure, (state, { error }): C3State => ({
        ...state,
        loadingState: state.loadingState - 1,
        error,
    })),
    // =========================================================
    on(C3Actions.LoadHardwareItemById.initial, (state): C3State => ({
        ...state,
        loadingState: state.loadingState + 1,
        error: null,
    })),
    on(C3Actions.LoadHardwareItemById.success, (state, { hardwareItem }): C3State => ({
        ...state,
        loadingState: state.loadingState - 1,
        hardwareItems: MergeArrayValues(state.hardwareItems, hardwareItem),
        error: null,
    })),
    on(C3Actions.LoadHardwareItemById.failure, (state, { error }): C3State => ({
        ...state,
        loadingState: state.loadingState - 1,
        error,
    })),
    // =========================================================
    on(C3Actions.LoadHardwareItemsByIds.initial, (state): C3State => ({
        ...state,
        loadingState: state.loadingState + 1,
        error: null,
    })),
    on(C3Actions.LoadHardwareItemsByIds.success, (state, { hardwareItems }): C3State => ({
        ...state,
        loadingState: state.loadingState - 1,
        hardwareItems,
        error: null,
    })),
    on(C3Actions.LoadHardwareItemsByIds.failure, (state, { error }): C3State => ({
        ...state,
        loadingState: state.loadingState - 1,
        error,
    })),
    // =========================================================
    on(C3Actions.LoadRecentHardwareInventoryTransactions.initial, (state): C3State => ({
        ...state,
        loadingState: state.loadingState + 1,
        error: null
    })),
    on(C3Actions.LoadRecentHardwareInventoryTransactions.success, (state, { transactions }): C3State => ({
        ...state,
        loadingState: state.loadingState - 1,
        inventoryTransactions: MergeArrayValues(state.inventoryTransactions, transactions),
        error: null,
    })),
    on(C3Actions.LoadRecentHardwareInventoryTransactions.failure, (state, { error }): C3State => ({
        ...state,
        loadingState: state.loadingState - 1,
        error,
    })),
    // =========================================================
    on(C3Actions.LoadVendorHardwareData.initial, (state): C3State => ({
        ...state,
        loadingState: state.loadingState + 1,
        error: null,
    })),
    on(C3Actions.LoadVendorHardwareData.success, (state, { vendorHardwareData }): C3State => ({
        ...state,
        loadingState: state.loadingState - 1,
        vendorHardwareData,
        error: null,
    })),
    on(C3Actions.LoadVendorHardwareData.failure, (state, { error }): C3State => ({
        ...state,
        loadingState: state.loadingState - 1,
        error,
    })),
    // =========================================================
    on(C3Actions.LoadVendorHardwareDataByHardwareItemId.initial, (state): C3State => ({
        ...state,
        loadingState: state.loadingState + 1,
        error: null,
    })),
    on(C3Actions.LoadVendorHardwareDataByHardwareItemId.success, (state, { vendorHardwareData }): C3State => ({
        ...state,
        loadingState: state.loadingState - 1,
        vendorHardwareData: MergeArrayValues(state.vendorHardwareData, vendorHardwareData),
        error: null
    })),
    on(C3Actions.LoadVendorHardwareDataByHardwareItemId.failure, (state, { error }): C3State => ({
        ...state,
        loadingState: state.loadingState - 1,
        error,
    })),
    // =========================================================
    on(C3Actions.LoadVendorHardwareDataByHardwareItemIds.initial, (state): C3State => ({
        ...state,
        loadingState: state.loadingState + 1,
        error: null,
    })),
    on(C3Actions.LoadVendorHardwareDataByHardwareItemIds.success, (state, { vendorHardwareData }): C3State => ({
        ...state,
        loadingState: state.loadingState - 1,
        vendorHardwareData,
        error: null
    })),
    on(C3Actions.LoadVendorHardwareDataByHardwareItemIds.failure, (state, { error }): C3State => ({
        ...state,
        loadingState: state.loadingState - 1,
        error,
    })),
    // =========================================================
    on(C3Actions.SetHardwareInventoryTransaction.initial, (state): C3State => ({
        ...state,
        loadingState: state.loadingState + 1,
        error: null
    })),
    // original
    on(C3Actions.SetHardwareInventoryTransaction.success, (state, { transaction }): C3State => {
        let updatedHardwareItem = { ...state.hardwareItems.find(h => h.id === transaction.entityId) } as C3HardwareItem
        updatedHardwareItem.qtyOnHand += transaction.change
        return {
            ...state,
            inventoryTransactions: MergeArrayValues(state.inventoryTransactions, transaction),
            hardwareItems: MergeArrayValues(state.hardwareItems, updatedHardwareItem),
            loadingState: state.loadingState - 1,
            error: null
        }
    }),
    on(C3Actions.SetHardwareInventoryTransaction.failure, (state, { error }): C3State => ({
        ...state,
        loadingState: state.loadingState - 1,
        error,
    })),
    // =========================================================
    on(C3Actions.SetVendorHardwareData.initial, (state): C3State => ({
        ...state,
        loadingState: state.loadingState + 1,
        error: null
    })),
    on(C3Actions.SetVendorHardwareData.success, (state, { vendorHardwareData }): C3State => ({
        ...state,
        loadingState: state.loadingState - 1,
        vendorHardwareData: MergeArrayValues(state.vendorHardwareData, vendorHardwareData),
        error: null
    })),
    on(C3Actions.SetVendorHardwareData.failure, (state, { error }): C3State => ({
        ...state,
        loadingState: state.loadingState - 1,
        error,
    })),
    // =========================================================
    on(C3Actions.SetVendorHardwareDataAndHardwareItem.initial, (state): C3State => ({
        ...state,
        loadingState: state.loadingState + 1,
        error: null
    })),
    on(C3Actions.SetVendorHardwareDataAndHardwareItem.success, (state, { vendorHardwareData, hardwareItem }): C3State => ({
        ...state,
        loadingState: state.loadingState - 1,
        vendorHardwareData: MergeArrayValues(state.vendorHardwareData, vendorHardwareData),
        hardwareItems: MergeArrayValues(state.hardwareItems, hardwareItem),
        error: null
    })),
    on(C3Actions.SetVendorHardwareDataAndHardwareItem.failure, (state, { error }): C3State => ({
        ...state,
        loadingState: state.loadingState - 1,
        error,
    })),
    // =========================================================
    on(C3Actions.LoadProducts.initial, (state): C3State => ({
        ...state,
        loadingState: state.loadingState + 1,
        error: null,
    })),
    on(C3Actions.LoadProducts.success, (state, { products }): C3State => ({
        ...state,
        loadingState: state.loadingState - 1,
        products,
        error: null,
    })),
    on(C3Actions.LoadProducts.failure, (state, { error }): C3State => ({
        ...state,
        loadingState: state.loadingState - 1,
        error,
    })),
    // =========================================================
    on(C3Actions.LoadOrders.initial, (state): C3State => ({
        ...state,
        loadingState: state.loadingState + 1,
        error: null,
    })),
    on(C3Actions.LoadOrders.success, (state, { orders }): C3State => ({
        ...state,
        loadingState: state.loadingState - 1,
        orders,
        error: null,
    })),
    on(C3Actions.LoadOrders.failure, (state, { error }): C3State => ({
        ...state,
        loadingState: state.loadingState - 1,
        error,
    })),
    // =========================================================
    on(C3Actions.LoadOrderedProducts.initial, (state): C3State => ({
        ...state,
        loadingState: state.loadingState + 1,
        error: null,
    })),
    on(C3Actions.LoadOrderedProducts.success, (state, { orderedProducts }): C3State => ({
        ...state,
        loadingState: state.loadingState - 1,
        orderedProducts,
        error: null,
    })),
    on(C3Actions.LoadOrderedProducts.failure, (state, { error }): C3State => ({
        ...state,
        loadingState: state.loadingState - 1,
        error,
    })),
    // =========================================================
    on(C3Actions.LoadReleasedAndDraftProductVersions.initial, (state): C3State => ({
        ...state,
        loadingState: state.loadingState + 1,
        error: null,
    })),
    on(C3Actions.LoadReleasedAndDraftProductVersions.success, (state, { releasedAndDraftProductVersions }): C3State => ({
        ...state,
        loadingState: state.loadingState - 1,
        productVersions: releasedAndDraftProductVersions,
        error: null,
    })),
    on(C3Actions.LoadReleasedAndDraftProductVersions.failure, (state, { error }): C3State => ({
        ...state,
        loadingState: state.loadingState - 1,
        error,
    })),
    // =========================================================
    on(C3Actions.LoadReleasedProductVersions.initial, (state): C3State => ({
        ...state,
        loadingState: state.loadingState + 1,
        error: null,
    })),
    on(C3Actions.LoadReleasedProductVersions.success, (state, { productVersions }): C3State => ({
        ...state,
        loadingState: state.loadingState - 1,
        productVersions,
        error: null,
    })),
    on(C3Actions.LoadReleasedProductVersions.failure, (state, { error }): C3State => ({
        ...state,
        loadingState: state.loadingState - 1,
        error,
    })),
    // =========================================================
    on(C3Actions.SetProduct.initial, (state): C3State => ({
        ...state,
        loadingState: state.loadingState + 1,
        error: null,
    })),
    on(C3Actions.SetProduct.success, (state, { product }): C3State => ({
        ...state,
        loadingState: state.loadingState - 1,
        products: MergeArrayValues(state.products, product),
        error: null
    })),
    on(C3Actions.SetProduct.failure, (state, { error }): C3State => ({
        ...state,
        loadingState: state.loadingState - 1,
        error,
    })),
    // =========================================================
    on(C3Actions.SetProductVersion.initial, (state): C3State => ({
        ...state,
        loadingState: state.loadingState + 1,
        error: null,
    })),
    on(C3Actions.SetProductVersion.success, (state, { productVersion }): C3State => ({
        ...state,
        loadingState: state.loadingState - 1,
        productVersions: MergeArrayValues(state.productVersions, productVersion),
        error: null
    })),
    on(C3Actions.SetProductVersion.failure, (state, { error }): C3State => ({
        ...state,
        loadingState: state.loadingState - 1,
        error,
    })),
    // =========================================================
    on(C3Actions.ViewProductLoadProductData.initial, (state): C3State => ({
        ...state,
        loadingState: state.loadingState + 1,
        error: null,
    })),
    on(C3Actions.ViewProductLoadProductData.success, (state , {
        selectedProductVersionId,
        selectedProduct,
        selectedProductVersions,
        lineItems,
        releasedProductVersions,
        products,
        hardwareItems,
        selectedDrawingResource,
        productVersionsReferencingSelectedProduct,
    }): C3State => {

        return {
            ...state,
            loadingState: state.loadingState - 1,
            productVersions: releasedProductVersions,
            products,
            hardwareItems,
            viewProductState: {
                ...state.viewProductState,
                selectedDrawingResource,
                selectedProductVersionId,
                selectedProduct,
                selectedProductVersions,
                billOfMaterialLineItems: lineItems,
                productVersionsReferencingSelectedProduct
            },
            error: null,
        }
    }),
    on(C3Actions.ViewProductLoadProductData.failure, (state, { error }): C3State => ({
        ...state,
        loadingState: state.loadingState - 1,
        error,
    })),
    // =========================================================
    on(C3Actions.SetBillOfMaterialsLineItems.initial, (state): C3State => ({
        ...state,
        loadingState: state.loadingState + 1,
        error: null,
    })),
    on(C3Actions.SetBillOfMaterialsLineItems.success, (state): C3State => ({
        ...state,
        loadingState: state.loadingState - 1,
        error: null
    })),
    on(C3Actions.SetBillOfMaterialsLineItems.failure, (state, { error }): C3State => ({
        ...state,
        loadingState: state.loadingState - 1,
        error,
    })),
    // =========================================================
    on(C3Actions.EditProductLoadProductData.initial, (state): C3State => ({
        ...state,
        loadingState: state.loadingState + 1,
        error: null,
    })),
    on(C3Actions.EditProductLoadProductData.success, (state, {
        product,
        productVersion,
        lineItems,
        products,
        releasedProductVersions,
        hardwareItems,
    }): C3State => ({
        ...state,
        loadingState: state.loadingState - 1,
        products,
        productVersions: releasedProductVersions,
        hardwareItems,
        billOfMaterialsLineItems: lineItems,
        editProductState: {
            ...state.editProductState,
            product,
            productVersion,
            saveStatus: SaveStatus.Saved
        }
    })),
    on(C3Actions.EditProductLoadProductData.failure, (state, { error }): C3State => ({
        ...state,
        loadingState: state.loadingState - 1,
        error,
    })),
    // =========================================================
    on(C3Actions.EditProductSaveProductVersion.initial, (state): C3State => ({
        ...state,
        loadingState: state.loadingState + 1,
        error: null
    })),
    on(C3Actions.EditProductSaveProductVersion.success, (state, { productVersion }): C3State => ({
        ...state,
        loadingState: state.loadingState - 1,
        editProductState: {
            ...state.editProductState,
            productVersion,
            saveStatus: SaveStatus.Saved
        }
    })),
    on(C3Actions.EditProductSaveProductVersion.failure, (state, { error }): C3State => ({
        ...state,
        loadingState: state.loadingState - 1,
        error
    })),
    // =========================================================
    on(C3Actions.EditProductDeleteDraftProductVersion.initial, (state): C3State => ({
        ...state,
        loadingState: state.loadingState + 1,
        error: null
    })),
    on(C3Actions.EditProductDeleteDraftProductVersion.success, (state, { productVersionId }): C3State => ({
        ...state,
        loadingState: state.loadingState - 1,
        productVersions: [...state.productVersions].filter(pv => pv.id !== productVersionId),
        error: null
    })),
    on(C3Actions.EditProductDeleteDraftProductVersion.failure, (state, { error }): C3State => ({
        ...state,
        loadingState: state.loadingState - 1,
        error,
    })),
    // =========================================================
    on(C3Actions.EditProduct.updatepartnumber, (state, { partNumber }): C3State => ({
        ...state,
        editProductState: {
            ...state.editProductState,
            productVersion: { ...state.editProductState.productVersion, partNumber }
        }
    })),
    on(C3Actions.EditProduct.updatecustomerpartnumber, (state, { customerPartNumber }): C3State => ({
        ...state,
        editProductState: {
            ...state.editProductState,
            productVersion: { ...state.editProductState.productVersion, customerPartNumber, }
        }
    })),
    on(C3Actions.EditProduct.updatecustomerid, (state, { customerId }): C3State => ({
        ...state,
        editProductState: {
            ...state.editProductState,
            productVersion: { ...state.editProductState.productVersion, customerId }
        }
    })),
    on(C3Actions.EditProduct.updatedescription, (state, { description }): C3State => ({
        ...state,
        editProductState: {
            ...state.editProductState,
            productVersion: { ...state.editProductState.productVersion, description, }
        }
    })),
    on(C3Actions.EditProduct.updateproducttype, (state, { productType }): C3State => ({
        ...state,
        editProductState: {
            ...state.editProductState,
            productVersion: { ...state.editProductState.productVersion, type: productType, }
        }
    })),
    on(C3Actions.EditProduct.updateimageurl, (state, { imageUrl }): C3State => ({
        ...state,
        editProductState: {
            ...state.editProductState,
            productVersion: { ...state.editProductState.productVersion, imageUrl, }
        }
    })),
    on(C3Actions.EditProduct.updatethumbnailimageurl, (state, { thumbnailImageUrl }): C3State => ({
        ...state,
        editProductState: {
            ...state.editProductState,
            productVersion: { ...state.editProductState.productVersion, thumbnailImageUrl, }
        }
    })),
    // ======================================================
    on(C3Actions.EditProduct.addlineitem, (state, { lineItem }): C3State => ({
        ...state,
        editProductState: {
            ...state.editProductState,
            billOfMaterialLineItems: MergeArrayValues(state.editProductState.billOfMaterialLineItems, lineItem)
        }
    })),
    on(C3Actions.EditProduct.addlineitems, (state, { lineItems }): C3State => ({
        ...state,
        editProductState: {
            ...state.editProductState,
            billOfMaterialLineItems: MergeArrayValues(state.editProductState.billOfMaterialLineItems, lineItems)
        }
    })),
    on(C3Actions.EditProduct.removelineitem, (state, { lineItemId }): C3State => ({
        ...state,
        billOfMaterialsLineItems: state.billOfMaterialsLineItems.filter(li => li.id !== lineItemId),
        editProductState: {
            ...state.editProductState,
            billOfMaterialLineItemIdsToDelete: state.editProductState.billOfMaterialLineItemIdsToDelete.includes(lineItemId)
                ? state.editProductState.billOfMaterialLineItemIdsToDelete
                : [...state.editProductState.billOfMaterialLineItemIdsToDelete, lineItemId],
            billOfMaterialLineItems: state.editProductState.billOfMaterialLineItems.filter(lineItem => lineItem.id != lineItemId)
        }
    })),
    on(C3Actions.EditProduct.updatelineitem, (state, { lineItem }): C3State => ({
        ...state,
        billOfMaterialsLineItems: state.billOfMaterialsLineItems.filter(li => li.id !== lineItem.id),
        editProductState: {
            ...state.editProductState,
            billOfMaterialLineItems: MergeArrayValues(state.editProductState.billOfMaterialLineItems, lineItem)
        }
    })),
    on(C3Actions.EditProduct.adddrawingresource, (state, { resourceId }): C3State => ({
        ...state,
        editProductState: {
            ...state.editProductState,
            productVersion: {
                ...state.editProductState.productVersion,
                drawingResourceId: resourceId
            }
        }
    })),
    on(C3Actions.EditProduct.removedrawingresource, (state): C3State => ({
        ...state,
        editProductState: {
            ...state.editProductState,
            productVersion: {
                ...state.editProductState.productVersion,
                drawingResourceId: '',
            }
        }
    })),
    // =========================================================
    on(C3Actions.SetEditProductSaveStatus, (state, { saveStatus }): C3State => ({
        ...state,
        editProductState: { ...state.editProductState, saveStatus }
    })),
    // =========================================================
    on(C3Actions.ViewProductClearState, (state): C3State => ({
        ...initialState
    })),
    // =========================================================
    on(C3Actions.ViewProductUpdate.updateselectedproduct, (state, { product }): C3State => ({
        ...state,
        viewProductState: {
            ...state.viewProductState,
            selectedProduct: product
        }
    })),
    on(C3Actions.ViewProductUpdate.updateselectedproductversion, (state, { productVersion }): C3State => ({
        ...state,
        viewProductState: {
            ...state.viewProductState,
            selectedProductVersions: MergeArrayValues(state.viewProductState.selectedProductVersions, productVersion)
        }
    })),
    on(C3Actions.ViewProductUpdate.setselectedproductversionid, (state, { productVersionId }): C3State => ({
        ...state,
        viewProductState: {
            ...state.viewProductState,
            selectedProductVersionId: productVersionId
        }
    })),
    // =========================================================
    on(C3Actions.LoadBillOfMaterialsLineItemsByProductVersionId.initial, (state): C3State => ({
        ...state,
        loadingState: state.loadingState + 1,
        error: null,
    })),
    on(C3Actions.LoadBillOfMaterialsLineItemsByProductVersionId.success, (state, { lineItems }): C3State => ({
        ...state,
        loadingState: state.loadingState - 1,
        billOfMaterialsLineItems: lineItems,
        error: null,
    })),
    on(C3Actions.LoadBillOfMaterialsLineItemsByProductVersionId.failure, (state, { error }): C3State => ({
        ...state,
        loadingState: state.loadingState - 1,
        error,
    })),
    // =========================================================
    on(C3Actions.ViewProductLoadSelectedProduct.initial, (state): C3State => ({
        ...state,
        loadingState: state.loadingState + 1,
    })),
    on(C3Actions.ViewProductLoadSelectedProduct.success, (state, { product }): C3State => ({
        ...state,
        loadingState: state.loadingState - 1,
        error: null,
        viewProductState: {
            ...state.viewProductState,
            selectedProduct: product,
        }
    })),
    on(C3Actions.ViewProductLoadSelectedProduct.failure, (state, { error }): C3State => ({
        ...state,
        loadingState: state.loadingState - 1,
        error,
    })),
    // =========================================================
    on(C3Actions.LoadProductsByIds.initial, (state): C3State => ({
        ...state,
        loadingState: state.loadingState + 1,
        error: null
    })),
    on(C3Actions.LoadProductsByIds.success, (state, { products }): C3State => ({
        ...state,
        loadingState: state.loadingState - 1,
        products,
        error: null
    })),
    on(C3Actions.LoadProductsByIds.failure, (state, { error }): C3State => ({
        ...state,
        loadingState: state.loadingState - 1,
        error,
    })),
    // =========================================================
    on(C3Actions.SetProductInventoryTransaction.initial, (state): C3State => ({
        ...state,
        loadingState: state.loadingState + 1,
        error: null
    })),
    on(C3Actions.SetProductInventoryTransaction.success, (state, {
        transaction,
        hardwareIdToQuantityChangeDict,
        productIdToQuantityChangeDict,
    }): C3State => {
        const productQuantityChangeDict: Record<string, number> = {
            ...productIdToQuantityChangeDict,
            [transaction.entityId]: (productIdToQuantityChangeDict[transaction.entityId] ?? 0) + transaction.change,
        };

        return {
            ...state,
            inventoryTransactions: MergeArrayValues(state.inventoryTransactions, transaction),
            hardwareItems: applyQuantityChanges(state.hardwareItems, hardwareIdToQuantityChangeDict),
            products: applyQuantityChanges(state.products, productQuantityChangeDict),
            viewProductState: {
                ...state.viewProductState,
                selectedProduct: applyQuantityChange(state.viewProductState.selectedProduct, productQuantityChangeDict),
            },
            editProductState: {
                ...state.editProductState,
                product: applyQuantityChange(state.editProductState.product, productQuantityChangeDict),
            },
            loadingState: state.loadingState - 1,
            error: null
        }
    }),
    on(C3Actions.SetProductInventoryTransaction.failure, (state, { error }): C3State => ({
        ...state,
        loadingState: state.loadingState - 1,
        error,
    })),
    // =========================================================
    on(C3Actions.SetComponentGroupBomInventoryTransaction.initial, (state): C3State => ({
        ...state,
        loadingState: state.loadingState + 1,
        error: null,
    })),
    on(C3Actions.SetComponentGroupBomInventoryTransaction.success, (state, {
        transactions,
        hardwareIdToQuantityChangeDict,
        productIdToQuantityChangeDict,
    }): C3State => ({
        ...state,
        inventoryTransactions: MergeArrayValues(state.inventoryTransactions, transactions),
        hardwareItems: applyQuantityChanges(state.hardwareItems, hardwareIdToQuantityChangeDict),
        products: applyQuantityChanges(state.products, productIdToQuantityChangeDict),
        viewProductState: {
            ...state.viewProductState,
            selectedProduct: applyQuantityChange(state.viewProductState.selectedProduct, productIdToQuantityChangeDict),
        },
        editProductState: {
            ...state.editProductState,
            product: applyQuantityChange(state.editProductState.product, productIdToQuantityChangeDict),
        },
        loadingState: state.loadingState - 1,
        error: null,
    })),
    on(C3Actions.SetComponentGroupBomInventoryTransaction.failure, (state, { error }): C3State => ({
        ...state,
        loadingState: state.loadingState - 1,
        error,
    })),
    // =========================================================
    on(C3Actions.EditProductReleaseProductVersion.initial, (state): C3State => ({
        ...state,
        loadingState: state.loadingState + 1,
        error: null,
    })),
    on(C3Actions.EditProductReleaseProductVersion.success, (state, { productVersion }): C3State => ({
        ...state,
        loadingState: state.loadingState - 1,
        editProductState: {
            ...state.editProductState,
            productVersion,
            saveStatus: SaveStatus.Saved
        },
        error: null,
    })),
    on(C3Actions.EditProductReleaseProductVersion.failure, (state, { error }): C3State => ({
        ...state,
        loadingState: state.loadingState - 1,
        error,
    })),
    // =========================================================
    on(C3Actions.LoadRecentProductInventoryTransactions.initial, (state): C3State => ({
        ...state,
        loadingState: state.loadingState + 1,
        error: null
    })),
    on(C3Actions.LoadRecentProductInventoryTransactions.success, (state, { transactions }): C3State => ({
        ...state,
        inventoryTransactions: MergeArrayValues(state.inventoryTransactions, transactions),
        loadingState: state.loadingState - 1,
        error: null
    })),
    on(C3Actions.LoadRecentProductInventoryTransactions.failure, (state, { error }): C3State => ({
        ...state,
        loadingState: state.loadingState - 1,
        error,
    })),
    // =========================================================
    
)
